import { useState, useRef, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';
import ReviewDetailModal from './ReviewDetailModal';
import './CustomerPhotoGallery.css';

const CustomerPhotoGallery = ({ reviews, onPhotoClick }) => {
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const galleryRef = useRef(null);

  // Handle horizontal scroll with mouse wheel
  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    const handleWheel = (e) => {
      // Only handle if there's horizontal scroll available
      if (gallery.scrollWidth > gallery.clientWidth) {
        e.preventDefault();
        
        // Use a multiplier for faster, smoother scrolling
        const scrollAmount = e.deltaY * 2;
        
        // Use smooth scrolling with requestAnimationFrame
        gallery.scrollBy({
          left: scrollAmount,
          behavior: 'auto' // Changed to 'auto' for instant response
        });
      }
    };

    gallery.addEventListener('wheel', handleWheel, { passive: false });
    return () => gallery.removeEventListener('wheel', handleWheel);
  }, []);

  // Collect all photos from all reviews with metadata
  const allPhotos = reviews
    .filter(review => review.images && review.images.length > 0)
    .flatMap(review =>
      review.images.map(image => ({
        imageUrl: image,
        rating: review.rating,
        reviewId: review._id,
        review: review
      }))
    );

  const handlePhotoClick = (photoIndex) => {
    // Open photo viewer with all photos, starting at clicked index
    onPhotoClick(allPhotos.map(p => p.imageUrl), photoIndex);
  };

  const handleSeeMoreClick = (e, photo) => {
    e.stopPropagation(); // Prevent photo click
    setSelectedReview(photo.review);
    setShowReviewModal(true);
  };

  if (allPhotos.length === 0) return null;

  return (
    <>
      <Card className="my-4">
        <Card.Body>
          <h4 className="mb-3">Customer Photos ({allPhotos.length})</h4>
          <div className="customer-photo-gallery" ref={galleryRef}>
            {allPhotos.map((photo, index) => (
              <div
                key={`${photo.reviewId}-${index}`}
                className="customer-photo-item"
                onClick={() => handlePhotoClick(index)}
              >
                <img src={photo.imageUrl} alt="Customer review" />
                
                {/* Star Rating Overlay */}
                <div className="photo-rating-overlay">
                  <FaStar className="rating-star" />
                  <span className="rating-number">{photo.rating.toFixed(1)}</span>
                </div>

                {/* See More Button */}
                <button
                  className="photo-see-more-btn"
                  onClick={(e) => handleSeeMoreClick(e, photo)}
                >
                  See More
                </button>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Review Detail Modal */}
      <ReviewDetailModal
        show={showReviewModal}
        review={selectedReview}
        onHide={() => setShowReviewModal(false)}
        onPhotoClick={onPhotoClick}
      />
    </>
  );
};

export default CustomerPhotoGallery;
